import React, { useState, useEffect } from 'react';
import { 
  Users, 
  Building2, 
  Edit3,
  Trash2
} from 'lucide-react';
import { Button, Card, CardContent, ConfirmDialog } from '../components/ui';
import { HotelWithProgress } from '../types';

import { UserManagement } from '../components/UserManagement';



export function Admin() {
  const [hotels, setHotels] = useState<HotelWithProgress[]>([]);
  const [activeTab, setActiveTab] = useState<'users' | 'hotels'>('users');
  const [deletingHotel, setDeletingHotel] = useState<HotelWithProgress | null>(null);


  useEffect(() => {
    loadHotels();
  }, []);

  const loadHotels = async () => {
    try {
      const data = await import('../services/api').then(module => module.apiService.getHotels());
      setHotels(data);
    } catch (err) {
      console.error('Error loading hotels:', err);
    }
  };







  const handleDeleteHotel = (hotelId: string) => {
    const hotel = hotels.find(h => h.id === hotelId);
    if (!hotel) return;
    
    setDeletingHotel(hotel);
  };

  const confirmDeleteHotel = async () => {
    if (!deletingHotel) return;
    
    try {
      const { apiService } = await import('../services/api');
      await apiService.deleteHotel(deletingHotel.id);
      setHotels(prev => prev.filter(h => h.id !== deletingHotel.id));
      setDeletingHotel(null);
    } catch (err) {
      console.error('Error deleting hotel:', err);
      setDeletingHotel(null);
    }
  };



  const tabs = [
    { id: 'users', label: 'Users', icon: Users, count: 0 },
    { id: 'hotels', label: 'Hotels', icon: Building2, count: hotels.length }
  ] as const;

  return (
    <div className="p-8">
      {/* Header */}
      <div className="mb-12">
        <div className="flex items-start justify-between">
          <div className="space-y-3">
            <h1 className="text-5xl font-display font-bold text-neutral-900 tracking-tight">Admin Panel</h1>
            <p className="text-neutral-700 text-lg font-text font-medium leading-relaxed max-w-2xl">
              Manage users, hotels, and system settings
            </p>
          </div>

        </div>
      </div>

      {/* Tab Navigation */}
      <div className="mb-8">
        <div className="flex items-center space-x-1 bg-white/30 backdrop-blur-xl rounded-2xl border border-neutral-200 p-2">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center space-x-3 px-6 py-3 rounded-xl font-semibold transition-all duration-200 ${
                activeTab === tab.id
                  ? 'bg-primary-500 text-white shadow-lg shadow-primary-500/25'
                  : 'text-neutral-600 hover:text-neutral-900 hover:bg-white/40'
              }`}
            >
              <tab.icon className="w-5 h-5" />
              <span>{tab.label}</span>
              {tab.count > 0 && (
                <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                  activeTab === tab.id ? 'bg-white/20' : 'bg-neutral-100'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="space-y-8">
        {/* Users Tab */}
        {activeTab === 'users' && (
          <UserManagement />
        )}



        {/* Hotels Tab */}
        {activeTab === 'hotels' && (
          <div className="space-y-6">
            <div className="space-y-2">
              <h2 className="text-3xl font-display font-bold text-neutral-900">Hotel Management</h2>
              <p className="text-neutral-600 font-text">Overview of all hotels in the system</p>
            </div>

            <Card className="bg-white/30 backdrop-blur-xl border-neutral-200 shadow-lg">
              <CardContent className="p-6">
                <div className="space-y-4">
                  {hotels.map((hotel) => (
                    <div 
                      key={hotel.id}
                      className="flex items-center justify-between p-4 bg-white/40 backdrop-blur-sm rounded-2xl border border-neutral-200 hover:bg-white/60 transition-all duration-200"
                    >
                      <div className="flex items-center space-x-4">
                        <div className="w-12 h-12 bg-gradient-to-br from-blue-100 to-blue-200 rounded-xl flex items-center justify-center">
                          <Building2 className="w-6 h-6 text-blue-600" />
                        </div>
                        <div className="space-y-1">
                          <h3 className="font-display font-bold text-neutral-900">{hotel.name}</h3>
                          <p className="text-neutral-600 font-text">Contact: {hotel.contactPerson}</p>
                          <p className="text-sm text-neutral-500 font-text">
                            Progress: {hotel.progressPercentage}% • {hotel.completedTasks}/{hotel.totalTasks} tasks
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Button variant="ghost" size="sm" className="text-neutral-600 hover:text-neutral-900">
                          <Edit3 className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => handleDeleteHotel(hotel.id)}
                          className="text-neutral-600 hover:text-red-500"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}




      </div>

      {/* Confirm Delete Hotel Dialog */}
      <ConfirmDialog
        isOpen={deletingHotel !== null}
        onClose={() => setDeletingHotel(null)}
        onConfirm={confirmDeleteHotel}
        title="Delete Hotel"
        message={deletingHotel ? `Are you sure you want to delete "${deletingHotel.name}"? This will also delete all associated tasks and cannot be undone.` : ''}
        variant="danger"
        confirmText="Delete Hotel"
      />
    </div>
  );
}
